import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import potencia from "../../static/media/Home/potencia.webp";
import resistencia from "../../static/media/Home/resistencia.webp";
import disciplina from "../../static/media/Home/disciplina.webp";

export default function TrainingBannerSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const cards = useMemo(
    () => [
      {
        key: "potencia",
        title: "Potencia",
        desc: "Desarrolla fuerza real y la capacidad de mover tu cuerpo con más control, energía y seguridad en cada entrenamiento.",
        img: potencia,
      },
      {
        key: "resistencia",
        title: "Resistencia",
        desc: "Mejora tu condición física para entrenar con más energía, soportar más esfuerzo y mantener un rendimiento constante.",
        img: resistencia,
      },
      {
        key: "disciplina",
        title: "Disciplina",
        desc: "Construye la mentalidad que transforma el esfuerzo en constancia y la constancia en resultados reales.",
        img: disciplina,
      },
    ],
    []
  );

  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!isMobile) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % cards.length);
    }, 3400);
    return () => clearInterval(id);
  }, [isMobile, cards.length]);

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#000",
        color: "#fff",
        py: { xs: 3, md: 5 },
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "clamp(24px, 10vw, 42px)", md: "clamp(48px, 3vw, 86px)" },
              lineHeight: { xs: 0.96, md: 0.92 },
              letterSpacing: { xs: -1.4, md: -2.8 },
              fontWeight: 750,
              textTransform: "uppercase",
              maxWidth: { md: 1200 },
            }}
          >
            Desarrolla tu
            <Box component="span" sx={{ color: "#e50914", display: "block" }}>
              verdadero potencial
            </Box>
          </Typography>

          <Typography
            sx={{
              mt: { xs: 1.8, md: 2.2 },
              fontSize: { xs: 15.5, md: 20 },
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.78)",
              maxWidth: { md: 1080 },
              fontWeight: 500,
            }}
          >
            En NovaXFit cada entrenamiento suma. Aquí vienes a mejorar tu rendimiento, ganar capacidad física y avanzar hacia una versión más preparada de ti.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: { xs: 0, md: 2.2 },
            alignItems: "stretch",
          }}
        >
          {cards.map((card, index) => {
            const show = !isMobile || active === index;

            return (
              <Box
                key={card.key}
                sx={{
                  display: show ? "block" : { xs: "none", md: "block" },
                  opacity: show ? 1 : 0,
                  transform: show ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity .45s ease, transform .45s ease",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: { xs: "60vh", sm: "70vh", md: 580 },
                    minHeight: { xs: 480, md: 620 },
                    borderRadius: { xs: 4, md: 5 },
                    overflow: "hidden",
                    bgcolor: "#000",
                  }}
                >
                  <Box
                    component="img"
                    src={card.img}
                    alt={card.title}
                    loading="lazy"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      transform: { xs: "scale(1.08)", md: "scale(1.1)" },
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: `
                        linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.88) 16%, rgba(0,0,0,0.28) 40%, rgba(0,0,0,0.08) 58%, rgba(0,0,0,0.38) 82%, rgba(0,0,0,0.9) 100%),
                        linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.12) 22%, rgba(0,0,0,0.12) 78%, rgba(0,0,0,0.92) 100%)
                      `,
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      p: { xs: 2.2, md: 2.8 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 70, md: 86 },
                        height: 4,
                        borderRadius: 999,
                        bgcolor: "#e50914",
                        mb: 1.4,
                        boxShadow: "0 0 24px rgba(229,9,20,0.45)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "clamp(34px, 5vw, 54px)", md: "clamp(34px, 2.2vw, 52px)" },
                        lineHeight: 0.95,
                        letterSpacing: -1.4,
                        fontWeight: 750,
                        textTransform: "uppercase",
                        color: "#fff",
                        textShadow: "0 10px 35px rgba(0,0,0,0.75)",
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1.2,
                        fontSize: { xs: 14, md: 16 },
                        lineHeight: 1.6,
                        color: "rgba(255,255,255,0.84)",
                        fontWeight: 500,
                        maxWidth: { md: 360 },
                        textShadow: "0 8px 24px rgba(0,0,0,0.72)",
                      }}
                    >
                      {card.desc}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            mt: { xs: 2, md: 0 },
            display: { xs: "flex", md: "none" },
            justifyContent: "center",
            gap: 1,
          }}
        >
          {cards.map((card, index) => (
            <Box
              key={card.key}
              sx={{
                width: index === active ? 28 : 10,
                height: 10,
                borderRadius: 999,
                bgcolor: index === active ? "#e50914" : "rgba(255,255,255,0.2)",
                transition: "all .25s ease",
                boxShadow: index === active ? "0 0 18px rgba(229,9,20,0.35)" : "none",
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
}